import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, User, Menu, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTranslation, useLanguage } from '../../contexts/LanguageContext';
import UserPanel from './UserPanel';
import type { UserRole } from '../../types';

const TopBar: React.FC = () => {
  const { user, switchRole, currentSemester } = useAuth();
  const { toggleSidebar, isMobile, isCollapsed } = useSidebar();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [roleOpen, setRoleOpen] = useState(false);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [userPanelTab, setUserPanelTab] = useState<'profile' | 'settings'>('profile');
  const [languageOpen, setLanguageOpen] = useState(false);
  const roleRef = useRef<HTMLDivElement | null>(null);
  const languageRef = useRef<HTMLDivElement | null>(null);

  // Format semester display (e.g., "20251" -> "2025/2026 Ganjil")
  const formatSemester = (semester: string): string => {
    if (!semester || semester.length !== 5) return 'Academic Year';
    
    const year = parseInt(semester.substring(0, 4));
    const semesterType = semester.substring(4);
    
    const nextYear = year + 1;
    const semesterLabel = semesterType === '1' ? 'Ganjil' : semesterType === '2' ? 'Genap' : semesterType === '3' ? 'Pendek' : '';
    
    return `${year}/${nextYear} ${semesterLabel}`;
  };

  const getRoleDisplayName = (role: UserRole): string => {
    switch (role) {
      case 'guest':
        return 'Guest';
      case 'staff':
        return 'Staff';
      case 'student_applicant':
        return 'Calon Mahasiswa';
      case 'student':
        return 'Mahasiswa';
      case 'lecturer':
        return 'Dosen';
      case 'alumni':
        return 'Alumni';
      case 'admin':
        return 'Admin';
      default:
        return role;
    }
  };

  // Get available roles, defaulting to all roles if not specified (for demo/guest users)
  const availableRoles: UserRole[] = user?.availableRoles || ['guest', 'staff', 'student_applicant', 'student', 'lecturer', 'alumni', 'admin'];

  const handleRoleSwitch = (newRole: UserRole) => {
    switchRole(newRole);
    setRoleOpen(false);
    
    // Redirect to the appropriate dashboard for the new role
    navigate(`/${newRole}/dashboard`);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (roleRef.current && !roleRef.current.contains(target)) setRoleOpen(false);
      if (languageRef.current && !languageRef.current.contains(target)) setLanguageOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setRoleOpen(false);
        setLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 transition-all duration-300 ${
        !isMobile && !isCollapsed ? 'lg:left-64' : 'lg:left-20'
      }`}>
        <div className="flex items-center justify-between gap-2 lg:gap-4 px-4 lg:px-6 py-4">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer lg:hidden flex-shrink-0"
              aria-label="Toggle menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}

          <div className="flex items-center flex-1 min-w-0 gap-2 sm:gap-4">
            {/* Show semester first on mobile, title first on desktop */}
            <div className="sm:hidden text-xs text-gray-500 whitespace-nowrap font-medium order-2">
              {formatSemester(currentSemester)}
            </div>
            <h1 className="text-lg lg:text-xl font-semibold text-gray-900 truncate hidden sm:block">
              ADB Academic System
            </h1>
            <div className="hidden sm:block text-xs lg:text-sm text-gray-500 whitespace-nowrap">
              {formatSemester(currentSemester)}
            </div>
          </div>

          <div className="flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
            {/* Language Switcher */}
            <div className="relative" ref={languageRef}>
              <button
                onClick={() => { setLanguageOpen(v => !v); setRoleOpen(false); }}
                aria-haspopup="menu"
                aria-expanded={languageOpen}
                className="flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <Globe className="h-4 w-4" />
                <span className="text-xs lg:text-sm">{t(`language.${language}`)}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {languageOpen && (
                <div role="menu" className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-[70]">
                  <div className="py-1">
                    <button
                      onClick={() => { setLanguage('id'); setLanguageOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-sm cursor-pointer ${
                        language === 'id' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {t('language.id')}
                    </button>
                    <button
                      onClick={() => { setLanguage('en'); setLanguageOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-sm cursor-pointer ${
                        language === 'en' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {t('language.en')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications */}
            <button className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
            </button>

            {/* Role Switcher - Only show if user has multiple roles */}
            {availableRoles.length > 1 && (
              <div className="relative" ref={roleRef}>
                <button
                  onClick={() => { setRoleOpen(v => !v); }}
                  aria-haspopup="menu"
                  aria-expanded={roleOpen}
                  className="flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  <span className="text-xs lg:text-sm">{getRoleDisplayName(user?.role || 'guest')}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {roleOpen && (
                  <div role="menu" className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[70]">
                    <div className="py-1">
                      {availableRoles.map((role) => (
                        <button
                          key={role}
                          onClick={() => handleRoleSwitch(role)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                        >
                          {getRoleDisplayName(role)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Menu - Click to open panel */}
            <button
              onClick={() => { setUserPanelTab('profile'); setShowUserPanel(true); setRoleOpen(false); }}
              className="flex items-center space-x-2 lg:space-x-3 px-2 lg:px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
            >
              <div className="h-8 w-8 bg-primary-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </div>
              <div className="text-left hidden sm:block">
                <div className="font-medium text-sm">{user?.name}</div>
                <div className="text-xs text-gray-500">{user?.email}</div>
              </div>
            </button>
          </div>
        </div>
      </header>
      
      {/* User Panel */}
      <UserPanel 
        isOpen={showUserPanel} 
        onClose={() => setShowUserPanel(false)}
        activeTab={userPanelTab}
        setActiveTab={setUserPanelTab}
      />
    </>
  );
};

export default TopBar;